import { motion } from 'framer-motion';
import { BookOpen, Smartphone, Globe, Apple, ScanLine, Sparkles, Search, Bookmark } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const APP_WEBSITE = 'https://theholywordbible.com';
const GOOGLE_PLAY = 'https://play.google.com/store/apps/details?id=app.emergent.biblialaunche1b2617f';
const APP_STORE = 'https://apps.apple.com/us/app/the-holy-word-bible/id6745786815';
const APP_ICON = 'https://customer-assets.emergentagent.com/job_1c2b9578-16c6-47ba-a745-b83594265996/artifacts/esfzzpyh_The%20Holy%20WORD%20Bible%20icon%202%20%28512%20x%20512%20px%29%20%281%29.png';

const features = [
  { icon: BookOpen, title: 'Read Anywhere', desc: 'Access the full Bible on your phone, tablet, or browser — anytime, anywhere.' },
  { icon: Search, title: 'Study Deeper', desc: 'Search scripture and explore the WORD in its true context.' },
  { icon: Bookmark, title: 'Save & Reflect', desc: 'Bookmark verses and keep your favorite passages close.' },
];

export const BibleApp = () => {
  return (
    <div data-testid="bible-app-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 relative overflow-hidden" data-testid="bible-app-hero">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://customer-assets.emergentagent.com/job_faith-revamp-1/artifacts/1x0fdbpc_Lion%20King%201.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/80 to-background" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Intro */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-gold/20 text-gold px-4 py-2 rounded-full text-sm mb-6">
                <Sparkles size={16} />
                <span>Our Official App</span>
              </div>
              <div className="flex items-center gap-4 mb-6">
                <img
                  src={APP_ICON}
                  alt="The Holy WORD Bible app icon"
                  className="w-20 h-20 rounded-2xl border border-gold/30 shadow-lg"
                  data-testid="app-icon-hero"
                />
                <h1 className="font-cinzel text-4xl sm:text-5xl lg:text-6xl text-foreground">
                  The Holy <span className="text-gold">WORD</span> Bible
                </h1>
              </div>
              <p className="text-lg text-muted-foreground mb-8 max-w-xl">
                Our Ministry's official app for reading and studying the Bible. Take the WORD with you everywhere — available on Google Play, the App Store, and the web.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href={GOOGLE_PLAY}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="app-google-play-btn"
                >
                  <Smartphone size={18} />
                  Google Play
                </a>
                <a
                  href={APP_STORE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="app-app-store-btn"
                >
                  <Apple size={18} />
                  App Store
                </a>
                <a
                  href={APP_WEBSITE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="app-website-btn"
                >
                  <Globe size={18} />
                  Visit Website
                </a>
              </div>
            </motion.div>

            {/* Right: App Icon + QR */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="card-dark gold-glow flex flex-col items-center text-center"
              data-testid="bible-app-qr-card"
            >
              <div className="w-20 h-20 rounded-2xl overflow-hidden border border-gold/30 mb-6">
                <img src={APP_ICON} alt="The Holy WORD Bible app icon" className="w-full h-full object-cover" />
              </div>
              <div className="inline-flex items-center gap-2 text-gold text-sm uppercase tracking-wider mb-4">
                <ScanLine size={16} />
                <span>Scan to Download</span>
              </div>
              <div className="bg-white p-4 rounded-lg" data-testid="bible-app-qr-code">
                <QRCodeSVG
                  value={APP_WEBSITE}
                  size={200}
                  level="M"
                  fgColor="#0A0A0A"
                  bgColor="#FFFFFF"
                />
              </div>
              <p className="text-muted-foreground text-sm mt-4 max-w-xs">
                Point your phone camera at the code to open The Holy WORD Bible and choose your store.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-cinzel text-3xl text-gold mb-4">Why You'll Love It</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Everything you need to read, study, and grow in the WORD — right in your pocket.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-dark text-center"
                data-testid={`app-feature-${index}`}
              >
                <div className="w-14 h-14 mx-auto rounded-full bg-gold/10 flex items-center justify-center mb-4">
                  <feature.icon className="text-gold" size={28} />
                </div>
                <h3 className="font-cinzel text-xl text-foreground mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-cinzel text-3xl sm:text-4xl text-foreground mb-6">
              Get The <span className="text-gold">WORD</span> Today
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Download The Holy WORD Bible and begin your study journey with us.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={GOOGLE_PLAY}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2"
              >
                <Smartphone size={18} />
                Google Play
              </a>
              <a
                href={APP_STORE}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2"
              >
                <Apple size={18} />
                App Store
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
